import React, { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";

/**
 * Configuration modal for the Pipeline tab. Lets the user rename column
 * labels and edit the Stage / Type / Priority dropdown options that get
 * used throughout the page. Backed by /api/settings.pipeline_config.
 */
export default function PipelineConfigModal({ config, onClose, onSave }) {
  const [cfg, setCfg] = useState(() => JSON.parse(JSON.stringify(config)));

  const setLabel = (k, v) =>
    setCfg((c) => ({ ...c, labels: { ...c.labels, [k]: v } }));

  const setListItem = (key, i, v) =>
    setCfg((c) => ({ ...c, [key]: c[key].map((x, idx) => (idx === i ? v : x)) }));

  const addListItem = (key) =>
    setCfg((c) => ({ ...c, [key]: [...(c[key] || []), "New"] }));

  const removeListItem = (key, i) =>
    setCfg((c) => ({ ...c, [key]: c[key].filter((_, idx) => idx !== i) }));

  const save = () => {
    onSave(cfg);
    onClose();
  };

  const fieldRow = (k, label) => (
    <div className="flex items-center gap-3" key={k}>
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground w-32 flex-shrink-0">
        {label}
      </div>
      <input
        value={cfg.labels?.[k] || ""}
        onChange={(e) => setLabel(k, e.target.value)}
        className="hud-input flex-1"
        data-testid={`pipe-label-${k}`}
      />
    </div>
  );

  const listEditor = (key, title) => (
    <div>
      <div className="kpi-label mb-3">{title}</div>
      <div className="space-y-2">
        {(cfg[key] || []).map((v, i) => (
          <div className="flex items-center gap-2" key={i}>
            <input
              value={v}
              onChange={(e) => setListItem(key, i, e.target.value)}
              className="hud-input flex-1"
              data-testid={`pipe-${key}-${i}`}
            />
            <button
              onClick={() => removeListItem(key, i)}
              className="text-muted-foreground hover:text-[hsl(var(--signal))]"
              aria-label="Remove"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button
          onClick={() => addListItem(key)}
          className="hud-btn !text-[10px] mt-2"
          data-testid={`pipe-add-${key}`}
        >
          <Plus size={11} /> ADD {title}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="pipeline-config-modal"
    >
      <div
        className="hud-frame w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="kpi-label">Configure</div>
            <h2 className="display text-2xl font-bold uppercase tracking-tight mt-2">
              Pipeline<span className="text-oud">_</span>setup.
            </h2>
            <p className="text-xs text-muted-foreground mt-2 max-w-md">
              Rename column labels and edit the dropdown options. Existing
              rows keep their old values until you change them.
            </p>
          </div>
          <button onClick={onClose} className="hud-btn !p-2" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="kpi-label mb-3">Page title</div>
            <input
              value={cfg.page_title || ""}
              onChange={(e) => setCfg({ ...cfg, page_title: e.target.value })}
              className="hud-input w-full mb-5"
              data-testid="pipe-page-title"
            />
            <div className="kpi-label mb-3">Column labels</div>
            <div className="space-y-2">
              {fieldRow("client", "Client")}
              {fieldRow("property", "Property/Project")}
              {fieldRow("deal_type", "Type")}
              {fieldRow("stage", "Stage")}
              {fieldRow("source", "Source")}
              {fieldRow("expected_close", "Exp. Close")}
              {fieldRow("price", "Price/Value")}
              {fieldRow("commission_pct", "Commission/Rate %")}
              {fieldRow("projected_commission", "Projected")}
              {fieldRow("closed", "Closed")}
              {fieldRow("closed_date", "Closed Date")}
              {fieldRow("next_follow_up", "Follow-up")}
              {fieldRow("priority", "Priority")}
              {fieldRow("status", "Status")}
              {fieldRow("notes", "Notes")}
            </div>
          </div>
          <div className="space-y-6">
            {listEditor("stages", "Stages")}
            {listEditor("deal_types", "Types")}
            {listEditor("priorities", "Priorities")}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-7">
          <button onClick={onClose} className="hud-btn" data-testid="pipe-cancel">
            CANCEL
          </button>
          <button onClick={save} className="hud-btn hud-btn-primary" data-testid="pipe-save">
            SAVE CONFIG
          </button>
        </div>
      </div>
    </div>
  );
}
